import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { EventService } from "../services/EventService";
import { Event } from "../models/Event";
import { useAuth } from "./AuthContext";
import { Filter } from "../models/Filter";
import { useMatter } from "./MatterContext";
import { clean } from "../utils/utils";
import { MatterService } from "../services/MatterService";
import { useSocket } from "./SocketContext";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";

interface EventContextProps {
  filter: Filter | null;
  setFilter: (filter: Filter | null) => void;
  events: Event[];
  selectedEvent: Event | null;
  createEvent: (
    eventData: Omit<
      Event,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "createdById"
      | "matterId"
      | "updatedById"
    >,
  ) => Promise<string>;
  updateEvent: (
    eventId: string,
    updatedData: Partial<Omit<Event, "_id" | "createdAt">>,
  ) => Promise<string>;
  deleteEventById: (eventId: string) => void;
  setSelectedEvent: (eventId: string) => void;
  loading: Loadings;
  error: Errors;
}

const EventContext = createContext<EventContextProps | undefined>(undefined);

export const useEvent = (): EventContextProps => {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error("useEvent must be used within a EventProvider");
  }
  return context;
};

interface Errors {
  list?: Error | null;
  details?: Error | null;
}
interface Loadings {
  list?: boolean;
  details?: boolean;
}

export const EventProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();

  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [loadings, setLoadings] = useState<Loadings>({
    list: false,
    details: false,
  });

  const fetchEvents = async () => {
    if (!user || !currentMatterId) return;

    try {
      const data = await MatterService.getEvents(currentMatterId);
      setEvents(data);
      setErrors((prev) => ({ ...prev, list: null }));
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, list: err }));
    } finally {
      setLoadings((prev) => ({ ...prev, list: false }));
    }
  };

  useEffect(() => {
    setLoadings((prev) => ({ ...prev, list: true }));
    fetchEvents();
  }, [user, currentMatterId]);

  useSocketEventByIds("events:changed", ["*"], () => {
    fetchEvents();
  });

  const setSelectedEventById = async (eventId: string) => {
    try {
      setLoadings((prev) => ({ ...prev, details: true }));
      const event = await EventService.getEvent(eventId);
      setSelectedEvent(event);
      setSelectedEventId(eventId);
      setErrors((prev) => ({ ...prev, details: null }));
    } catch (err: any) {
      setSelectedEvent(null);
      setErrors((prev) => ({ ...prev, details: err }));
    } finally {
      setLoadings((prev) => ({ ...prev, details: false }));
    }
  };

  const createEvent = async (
    eventData: Omit<
      Event,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "createdById"
      | "matterId"
      | "updatedById"
    >,
  ): Promise<string> => {
    const newEventId = await EventService.createEvent({
      ...eventData,
      matterId: currentMatterId!,
    });
    return newEventId;
  };

  const updateEvent = async (
    eventId: string,
    updatedData: Partial<Omit<Event, "_id" | "createdAt">>,
  ): Promise<string> => {
    await EventService.updateEvent(eventId, clean(updatedData));
    setSelectedEventById(eventId); // refresh the view if it's the selected one
    return eventId;
  };

  const deleteEventById = async (eventId: string) => {
    if (!currentMatterId) return;
    await EventService.deleteEvent(eventId);
    setEvents((prev) => prev.filter((event) => event._id !== eventId));
    setSelectedEvent(null);
  };

  return (
    <EventContext.Provider
      value={{
        filter,
        setFilter,
        events,
        selectedEvent,
        setSelectedEvent: setSelectedEventById,
        loading: loadings,
        error: errors,
        createEvent,
        updateEvent,
        deleteEventById,
      }}
    >
      {children}
    </EventContext.Provider>
  );
};
